import React from "react";

import {
  GroupScores,
  ResilienceReport,
  ScoreTile,
} from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import {
  K,
  LimitedDataset,
  Section,
  useDataThrough,
  useDomainName,
} from "./parts";
import {
  band,
  Band,
  DOMAINS,
  formatGroupScore,
  formatScore,
} from "./resilience-view";
import styles from "./resilience.module.scss";

const Cell = ({
  tile,
  format,
}: {
  tile: ScoreTile;
  format: (value: number) => string;
}) => {
  const { t } = useTranslation(["admin"]);
  if (tile.suppressed) {
    return (
      <td className={styles["heat-cell"]}>
        <span aria-hidden="true">{t(`${K}.cohorts.hiddenScore`)}</span>
        <span className={styles["visually-hidden"]}>
          {t(`${K}.notEnoughData`)}
        </span>
      </td>
    );
  }
  return (
    <td className={styles["heat-cell"]} data-band={band(tile.value)}>
      <span className={styles["heat-chip"]}>{format(tile.value)}</span>
    </td>
  );
};

const Row = ({
  name,
  group,
  format,
}: {
  name: string;
  group: GroupScores;
  format: (value: number) => string;
}) => (
  <tr>
    <th scope="row" className={styles["heat-name"]}>
      {name}
    </th>
    {DOMAINS.map((domain) => (
      <Cell key={domain} tile={group.domains[domain]} format={format} />
    ))}
    <Cell tile={group.index} format={format} />
  </tr>
);

const BANDS: Band[] = ["steady", "watching", "nurturing"];

const Legend = () => {
  const { t } = useTranslation(["admin"]);
  return (
    <ul className={styles["heat-legend"]} aria-label={t(`${K}.legend.label`)}>
      {BANDS.map((one) => (
        <li key={one} className={styles["heat-legend-item"]} data-band={one}>
          <span className={styles["visually-hidden"]}>
            {t(`${K}.bands.${one}`)}{" "}
          </span>
          {t(`${K}.heatmap.legend.${one}`)}
        </li>
      ))}
    </ul>
  );
};

const Heatmap = ({ report }: { report: ResilienceReport }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const domainName = useDomainName();
  const dataThrough = useDataThrough();
  const selected = report.scores.selected;
  const units = selected.segments.business_unit;
  // Platform leaves out units too small to show, without a count; a unit it
  // sends can still have any score hidden, and is listed while any shows.
  const shown = Object.keys(units)
    .filter(
      (name) =>
        !units[name].index.suppressed ||
        DOMAINS.some((domain) => !units[name].domains[domain].suppressed)
    )
    .sort((a, b) => a.localeCompare(b, i18n.language));
  const companyWide = shown.length === 0;

  return (
    <Section
      title={t(`${K}.sections.heatmap`)}
      info={t(`${K}.heatmap.info`)}
      eyebrow={<Legend />}
    >
      {companyWide ? (
        <LimitedDataset
          section={t(`${K}.heatmap.breakdown`)}
          message={t(`${K}.heatmap.limited`)}
        />
      ) : (
        <p className={styles["card-description"]}>
          {t(`${K}.heatmap.description`)}
        </p>
      )}
      <div
        className={styles["heat-scroll"]}
        role="group"
        aria-label={t(`${K}.heatmap.caption`)}
        tabIndex={0}
      >
        <table className={styles["heat"]}>
          <caption className={styles["visually-hidden"]}>
            {t(`${K}.heatmap.caption`)}
          </caption>
          <thead>
            <tr>
              <th scope="col" className={styles["heat-name"]}>
                {t(`${K}.heatmap.department`)}
              </th>
              {DOMAINS.map((domain) => (
                <th key={domain} scope="col">
                  {domainName(domain)}
                </th>
              ))}
              <th scope="col">{t(`${K}.heatmap.overall`)}</th>
            </tr>
          </thead>
          <tbody>
            {companyWide ? (
              <Row
                name={t(`${K}.heatmap.companyWide`)}
                group={selected.all_staff}
                format={(value) => formatScore(value, i18n.language)}
              />
            ) : (
              shown.map((name) => (
                <Row
                  key={name}
                  name={name}
                  group={units[name]}
                  format={(value) => formatGroupScore(value, i18n.language)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className={styles["card-footnote"]}>
        {dataThrough(report.period_value)}
      </p>
    </Section>
  );
};

export default Heatmap;
